import React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { 
  ClassicEditor, 
  Heading,        
  Bold, 
  Italic,
  Underline,      
  FontColor,      
  FontBackgroundColor,    
  Essentials, 
  Mention, 
  Paragraph, 
  Undo, 
  List,        
  ListProperties,
  Table,               
  TableToolbar,        
  TableProperties,     
  TableCellProperties,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageInsert,
  SourceEditing,
  Alignment // <-- Added import for Alignment
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import API from '../../api/api'; // Application-wide Axios instance with pre-configured interceptors


/**
 * Custom CKEditor 5 Upload Adapter Plugin
 * * Intercepts default image insertion events within the editor and bridges them 
 * directly to the app's internal authenticated Axios instance (`API`).
 *
 * @param {ClassicEditor} editor - The live CKEditor instance provided at runtime.
 */
function MyAxiosUploadAdapterPlugin(editor) {
    // Override the core 'FileRepository' plugin factory method with our custom adapter blueprint
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return {
            /**
             * Triggers automatically when a user drags/drops, pastes, or selects an image file.
             * @returns {Promise<{ default: string }>} Resolves with an object mapping the S3 image path.
             */
            upload() {
                return loader.file.then((file) => {
                    const formData = new FormData();
                    
                    // Backend Key Requirement: CKEditor core specifically transmits binary file strings using the 'upload' parameter string
                    formData.append('upload', file); 

                    // Forwarding the payload through our predefined Axios connection instance.
                    // This route implicitly inherits global app interceptors (Authorization JWT tokens, X-Requested-With, X-Company-Id).
                    return API.post("/upload/cke/image/aws", formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                    .then((response) => {
                        // The custom Laravel Lumen Controller expects to return JSON formatted exactly as: { "url": "https://..." }
                        // CKEditor strictly expects an output format containing a 'default' mapping key pointing to the target URL asset.
                        return {
                            default: response.data.url
                        };
                    });
                });
            },
            
            /**
             * Fires if an image upload sequence is cancelled by the user mid-transmission 
             * or if the component is unexpectedly unmounted.
             */
            abort() {
                // Abort logic can be handled here if an AbortController is tied into the Axios call.
            }
        };
    };
}


/**
 * Reusable Rich Text Editor Component (CKEditor 5 Classic Build)
 * * Features: Rich Text Formatting, Advanced HTML Tables, Modular Image Resizing, 
 * Dynamic Source HTML editing view, and an automated AWS S3 Cloud Image Uploader.
 * * @param {Object} props
 * @param {string} props.value - Controlled state variable passing HTML text content down to the editor.
 * @param {Function} props.setValue - Trigger callback method dispatched up to sync state content changes.
 */
function AppCKEditor({ value, setValue }) {
    return (
        <div style={{ maxWidth: '' }}>
            {/* Scoped CSS Overrides: Applied safely internally to protect app parent markup structures */}
            <style>{`
                /* Keeps dropdown dropdown menus and dialog pickers overlaying cleanly above active view modals */
                .ck-body-wrapper { z-index: 9999 !important; }
                /* Sets an explicitly readable view height framework for the input text context box */
                .ck-editor__editable_inline { min-height: 650px; }
            `}</style>
            
            <CKEditor
                editor={ClassicEditor}
                data={value || ""} 
                config={{
                    licenseKey: 'GPL',
                    
                    // 1. Toolbars and Operational Layout Mapping
                    toolbar: {
                        items: [
                            'sourceEditing', '|', // Toggles view between rich-text GUI visual formatting and raw inline HTML code
                            'undo', 'redo', '|', 
                            'heading', '|', 
                            'bold', 'italic', 'underline', '|', 
                            'alignment', '|', // <-- Added Text Alignment dropdown item here
                            'fontColor', 'fontBackgroundColor', '|',
                            'bulletedList', 'numberedList', '|',
                            'insertTable', 'insertImage', '|' 
                        ]
                    },
                    
                    // 2. Core Functional Core Extensions & Capabilities
                    plugins: [
                        Essentials, Heading, Bold, Italic, Underline, FontColor, FontBackgroundColor,
                        Mention, Paragraph, Undo, List, ListProperties,
                        Table, TableToolbar, TableProperties, TableCellProperties,
                        Image, ImageToolbar, ImageCaption, ImageStyle, ImageResize, ImageInsert,
                        SourceEditing,
                        Alignment // <-- Registered the Alignment plugin
                    ],
                    
                    // 3. Custom Extensions Runtime Injection
                    extraPlugins: [MyAxiosUploadAdapterPlugin],
                    
                    // 4. Feature Configuration Sub-Blocks
                    heading: {
                        options: [
                            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
                        ]
                    },
                    alignment: {
                        // Optional explicit configuration: Left, Center, Right, and Justify options
                        options: [ 'left', 'center', 'right', 'justify' ]
                    },
                    list: {
                        properties: { styles: true, startIndex: true, reversed: true }
                    },
                    table: {
                        contentToolbar: [
                            'tableColumn', 'tableRow', 'mergeTableCells', 
                            'tableProperties', 'tableCellProperties'
                        ]
                    },
                    image: {
                        toolbar: [
                            'imageTextAlternative', '|', 
                            'imageStyle:inline', 'imageStyle:block', 'imageStyle:side', '|', 
                            'toggleImageCaption', 'resizeImage'
                        ]
                    }
                }}
                onChange={(event, editor) => {
                    const data = editor.getData();
                    setValue(data); // Propagates internal stringified HTML payload data out to the parent view state
                }}
            />
        </div>
    );
}

export default AppCKEditor;


/* ===================================================================================================
   TECHNICAL ARCHITECTURE DOCUMENTATION: HOW THIS EDITOR OPERATES
   ===================================================================================================

   1. PLUGIN-BASED LAYER DIVISION
      - CKEditor 5 isolates UI features (the "Image" or "SourceEditing" plugins) from data pipelines.
      - The 'SourceEditing' plugin changes the view window to display raw HTML strings instead of parsed DOM components.
      - The 'ImageInsert' plugin handles the file picker UI overlay, but remains agnostic about database/cloud targets.

   2. DRIVER EXTENSION INJECTION VIA EXTRA-PLUGINS
      - The 'extraPlugins' option functions as an lifecycle configuration hook.
      - On component instantiation, 'MyAxiosUploadAdapterPlugin' maps a custom object into the editor's core 'FileRepository'.
      - By overriding 'FileRepository.createUploadAdapter', we swap the default uploader driver for our custom Axios system.

   3. FILE PROCESSING PIPELINE LIFE-CYCLE
      A. Interaction: A user initiates an image selection via drag-drop, copy-paste, or the toolbar button file dialogue.
      B. Capture: The 'FileRepository' catches the binary file stream and hands it to our registered custom adapter object.
      C. Wrapper Translation: The adapter encapsulates the file into a JS 'FormData' container using the strict form key named 'upload'.
      D. Network Request: The runtime invokes our global instance: 'API.post("/upload/cke/image/aws", formData)'.
      E. Global Security Context: Because it utilizes our global Axios pipeline instead of basic fetch strings, the outbound HTTP call 
         implicitly standardizes with interceptors for Authorization (Bearer Tokens), X-Requested-With contexts, and Company IDs.
      F. Server Handshake: The Laravel Lumen controller receives the binary stream, constructs custom safe naming paths via S3, and 
         returns an endpoint payload formatted as: { "url": "https://..." }.
      G. Editor Finalization: The adapter extracts 'response.data.url', maps it to the key required by CKEditor ('default'), and hands it 
         back to the engine. CKEditor finishes the task by inserting a formatted HTML asset string (`<img src="...">`) directly into the final document markup.
   =================================================================================================== */